/**
 * Screenshot Protection Utility
 * 
 * Controls screenshot blocking based on environment and user role
 * - Development/Staging: Screenshots ALLOWED
 * - Admin users: Screenshots ALLOWED
 * - Test users: Screenshots ALLOWED
 * - Production regular users: Screenshots BLOCKED
 */

const ADMIN_EMAILS = [
  'support@healthloop.app',
];

export const shouldAllowScreenshots = (user = null) => {
  // 1. Check environment variable (highest priority)
  const allowScreenshots = process.env.REACT_APP_ALLOW_SCREENSHOTS === 'true';
  if (allowScreenshots) {
    console.log('📸 Screenshots ENABLED: Environment flag set');
    return true;
  }

  // 2. Check if environment is staging/development
  const environment = process.env.REACT_APP_ENVIRONMENT || 'production';
  if (environment === 'development' || environment === 'staging') {
    console.log(`📸 Screenshots ENABLED: ${environment} environment`);
    return true;
  }

  // 3. Check if user is admin
  if (user && user.email) {
    const isAdmin = ADMIN_EMAILS.includes(user.email.toLowerCase());
    if (isAdmin) {
      console.log('📸 Screenshots ENABLED: Admin user');
      return true;
    }

    // Check if user has admin role
    if (user.role === 'admin') {
      console.log('📸 Screenshots ENABLED: Admin role');
      return true;
    }

    // Check if email contains 'test' or 'demo'
    const isTestUser = user.email.toLowerCase().includes('test') || 
                       user.email.toLowerCase().includes('demo');
    if (isTestUser) {
      console.log('📸 Screenshots ENABLED: Test user');
      return true;
    }
  }

  // 4. Default: Block screenshots for production users
  console.log('🔒 Screenshots BLOCKED: Production user');
  return false;
};

export const applyScreenshotProtection = (allow = false) => {
  const root = document.documentElement;
  
  if (allow) {
    // Remove screenshot blocking styles
    root.style.removeProperty('-webkit-user-select');
    root.style.removeProperty('-moz-user-select');
    root.style.removeProperty('-ms-user-select');
    root.style.removeProperty('user-select');
    root.classList.remove('screenshot-blocked');
    console.log('✅ Screenshot protection DISABLED');
  } else {
    // Apply screenshot blocking styles
    root.style.setProperty('-webkit-user-select', 'none', 'important');
    root.style.setProperty('-moz-user-select', 'none', 'important');
    root.style.setProperty('-ms-user-select', 'none', 'important');
    root.style.setProperty('user-select', 'none', 'important');
    root.classList.add('screenshot-blocked');
    console.log('🔒 Screenshot protection ENABLED');
    
    // Disable right-click context menu
    document.addEventListener('contextmenu', preventContextMenu);
    
    // Disable common screenshot shortcuts
    document.addEventListener('keydown', preventScreenshotShortcuts);
    
    // Add watermark overlay for production
    addWatermarkOverlay();
  }
};

const preventContextMenu = (e) => {
  e.preventDefault();
  console.log('🚫 Right-click disabled (screenshot protection)');
  return false;
};

const preventScreenshotShortcuts = (e) => {
  // Prevent Print Screen, Cmd+Shift+3/4/5 (Mac), Windows+Shift+S
  if (
    e.key === 'PrintScreen' ||
    (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) ||
    (e.key === 's' && e.metaKey && e.shiftKey)
  ) {
    e.preventDefault();
    console.log('🚫 Screenshot shortcut blocked');
    return false;
  }
};

const addWatermarkOverlay = () => {
  // Check if watermark already exists
  if (document.getElementById('screenshot-watermark')) return;
  
  const watermark = document.createElement('div');
  watermark.id = 'screenshot-watermark';
  watermark.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 9999;
    opacity: 0.03;
    background-image: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 100px,
      rgba(0, 0, 0, 0.1) 100px,
      rgba(0, 0, 0, 0.1) 200px
    );
  `;
  watermark.setAttribute('data-watermark', 'CONFIDENTIAL');
  document.body.appendChild(watermark);
};

export const removeScreenshotProtection = () => {
  // Remove event listeners
  document.removeEventListener('contextmenu', preventContextMenu);
  document.removeEventListener('keydown', preventScreenshotShortcuts);
  
  // Remove watermark
  const watermark = document.getElementById('screenshot-watermark');
  if (watermark) {
    watermark.remove();
  }
  
  // Remove CSS classes
  document.documentElement.classList.remove('screenshot-blocked');
};

// Development helper - force enable screenshots
export const enableScreenshotsForTesting = () => {
  console.log('🧪 DEVELOPER MODE: Screenshots force-enabled for testing');
  applyScreenshotProtection(true);
  removeScreenshotProtection();
};

// Add to window for easy debugging
if (typeof window !== 'undefined') {
  window.enableScreenshots = enableScreenshotsForTesting;
}

export default {
  shouldAllowScreenshots,
  applyScreenshotProtection,
  removeScreenshotProtection,
  enableScreenshotsForTesting
};
